/**
 * context/UserContext.jsx
 *
 * Real authentication context backed by the /api/auth/me endpoint.
 *
 * State lifecycle:
 *   loading=true  → fetching /api/auth/me on app startup
 *   user=null     → 401 from /me  → show LoginPage
 *   user.role='pending' → show PendingPage
 *   user active   → show the app
 *
 * Exported hook: useUser()
 *   { user, role, loading, isAuthenticated, isAdmin, isOwner, can, logout }
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user,    setUser]    = useState(null);   // null = unchecked or not logged in
  const [loading, setLoading] = useState(true);   // true only on first mount
  const heartbeatRef = useRef(null);

  // ── Check session on mount ─────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${BASE}/api/auth/me`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401) return null;         // not logged in
        if (r.ok) return r.json();
        return null;
      })
      .then(data => {
        setUser(data || null);
        setLoading(false);
      })
      .catch(() => {
        setUser(null);
        setLoading(false);
      });
  }, []);

  // ── Heartbeat every 5 min (active users only) ──────────────────────────────
  useEffect(() => {
    if (!user || user.role === 'pending') {
      clearInterval(heartbeatRef.current);
      return;
    }
    const ping = () =>
      fetch(`${BASE}/api/auth/activity/heartbeat`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    // Ping immediately on login, then every 5 min
    ping();
    heartbeatRef.current = setInterval(ping, 5 * 60_000);
    return () => clearInterval(heartbeatRef.current);
  }, [user?.google_id, user?.role]);  // re-run only when identity changes

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await fetch(`${BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch { /* ignore — cookie will expire naturally */ }
    setUser(null);
    // Hard redirect clears any in-memory state cleanly
    window.location.href = '/';
  }, []);

  // ── Activity event (tab navigation) ───────────────────────────────────────
  const trackTabVisit = useCallback((tab) => {
    if (!user || user.role === 'pending') return;
    fetch(`${BASE}/api/auth/activity/event?tab=${tab}&action=tab_visit`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  }, [user?.google_id, user?.role]);

  // ── Permission helper ──────────────────────────────────────────────────────
  // Returns true if the current user can access a given tab key.
  // owner / user always return true (is_admin flag from backend covers FULL_ACCESS_ROLES).
  // For other roles, checks the per-role permission map returned by /me.
  const can = useCallback((tabKey) => {
    if (!user || user.role === 'pending') return false;
    if (user.is_admin) return true;
    return user.permissions?.[tabKey] === true;
  }, [user]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const role            = user?.role  ?? null;
  const isAdmin         = user?.is_admin === true;    // true for owner + user (full access)
  const isOwner         = user?.role === 'owner';     // F3: true only for owner role
  const isAuthenticated = !!user && role !== 'pending';

  return (
    <UserContext.Provider value={{
      user,
      role,
      loading,
      isAuthenticated,
      isAdmin,
      isOwner,          // true only for owner role (use for Settings management gates)
      can,
      logout,
      trackTabVisit,
      // stub so old code that called setRole() doesn't crash
      setRole: () => {},
    }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
